// @flow
import * as React from 'react';
import styled from 'styled-components';
import Form, {
  GeneralError,
  SubmitButton,
  InputField,
  HiddenField,
  RadioButtonsField,
  DollarInputField,
  CreditCardField,
  CheckboxField
} from 'components/Form';
import AddressWithoutLocationForm from 'components/Form/AddressWithoutLocationForm';
import Divider from 'components/Divider';
import Icon from 'components/Icon';
import { H3 } from 'components/Text';
import { trackClicked } from 'utils/analytics';
import { transition, gutterWidth } from 'theme';

const AMOUNTS = [
  { label: '$25', value: 25 },
  { label: '$50', value: 50 },
  { label: '$100', value: 100 },
  { label: getText('Other Amount'), value: 'OTHER' }
];

const StyledSubmitButton = styled(SubmitButton)`
  svg {
    transition: transform ${transition};
  }
  &:hover,
  &:focus {
    svg {
      transform: translateX(50%);
    }
  }
`;

const StyledDivider = props => (
  <Divider light dashed my={[gutterWidth / 2, null, gutterWidth]} {...props} />
);

const DonateForm = ({
  onSubmit,
  campaignId,
  initialValues
}: {
  onSubmit: Function,
  campaignId: string,
  initialValues: Object
}) => (
  <Form
    data-qa-id="donate-form"
    initialValues={initialValues}
    onSubmit={onSubmit}
    render={({ getValue }) => (
      <>
        <GeneralError />
        <H3>{getText('Your Donation')}</H3>
        <HiddenField name="campaignId" value={campaignId} />
        <RadioButtonsField
          buttons={AMOUNTS}
          name="amount"
          label={getText('How much would you like to donate?')}
          onUpdateValue={v =>
            trackClicked('Preset Donation Amount', {
              amount: v,
              campaign_id: campaignId
            })
          }
          square
        />
        {getValue('amount') === 'OTHER' && (
          <DollarInputField
            name="otherAmount"
            min={5}
            errorMessage={getText('Please enter an amount of $5 or more')}
          />
        )}
        <StyledDivider />
        <H3>{getText('Payment Info')}</H3>
        <CreditCardField wrap />
        <StyledDivider />
        <H3>{getText('Billing Info')}</H3>
        <InputField
          name="firstName"
          label={getText('First Name')}
          placeholder={getText('Casey')}
        />
        <InputField
          name="lastName"
          label={getText('Last Name')}
          placeholder={getText('Smith')}
        />
        <AddressWithoutLocationForm
          useUSStateDropdown={getValue('country') === 'US'}
        />
        <StyledDivider
          mt={[`${gutterWidth / 2 - 16}px`, null, gutterWidth - 30]}
        />
        <H3>{getText('Contact Info')}</H3>
        <InputField
          type="email"
          name="email"
          placeholder={getText('casey.smith@example.com')}
          label={getText('Email')}
        />
        <InputField
          name="phone"
          placeholder={getText('e.g. 123 555-1234')}
          label={getText('Phone Number (optional)')}
        />
        <StyledDivider />
        <CheckboxField
          name="isForHonoree"
          label={getText('Dedicate this gift in honor or in memory of someone')}
        />
        {getValue('isForHonoree') && (
          <>
            <H3>{getText('Who is this gift dedicated to?')}</H3>
            <InputField
              name="honoreeFirstName"
              label={`${getText(`First Name`)}*`}
              placeholder={getText('Casey')}
            />
            <InputField
              name="honoreeLastName"
              label={`${getText(`Last Name`)}*`}
              placeholder={getText('Smith')}
            />
            <InputField
              name="honoreeEmail"
              type="email"
              label={getText('Email for E-card (optional)')}
              description={getText(
                'An e-card will be sent to the email address provided'
              )}
              placeholder={getText('casey.smith@example.com')}
            />
            <StyledDivider />
          </>
        )}

        <StyledSubmitButton
          mt={[gutterWidth / 2, null, gutterWidth]}
          p="15px 0"
          width={1}
          size="large"
          square
        >
          {getText('Donate')}
          <Icon
            name="arrow"
            width={14}
            height={14}
            ml={12}
            fill="currentColor"
          />
        </StyledSubmitButton>
      </>
    )}
  />
);

export default DonateForm;
